/**
 * Moya Platform Shell
 *
 * A Web Components library providing unified platform UI components
 * for the Moya ecosystem. Framework-agnostic, works with React, Vue,
 * Angular, or vanilla JavaScript.
 *
 * Usage:
 * ```html
 * <script type="module">
 *   import '@moya/platform-shell';
 * </script>
 *
 * <moya-platform-header
 *   api-url="https://api.moyasolutions.com"
 *   ws-url="wss://notifications.moyasolutions.com"
 *   notifications-url="https://notify.moyasolutions.com"
 *   messenger-url="wss://messenger.moyasolutions.com/api/v1/ws"
 *   token="your-jwt-token"
 *   app-id="endpoints"
 *   app-name="Endpoints Dashboard"
 *   show-search
 *   search-placeholder="Search endpoints..."
 *   show-messenger
 *   avatar-only
 * ></moya-platform-header>
 * ```
 *
 * Notifications live on their own service. `api-url` / `configure()` does NOT
 * cover them -- call `configureNotifications('https://notify.moyasolutions.com')`
 * as well, or the bell will send /api/v1/notifications/* to whatever else the
 * app configured and get a 404 on every poll.
 */

import { hydrateSessionFromCookie } from './utils/sso-hydration';

// =============================================================================
// Cross-subdomain SSO: hydrate localStorage from domain cookie IMMEDIATELY.
// This MUST run before any app router guard checks localStorage for a token.
// The .moyasolutions.com cookie is set by the SSO portal on login.
// =============================================================================
// The logic lives in ./utils/sso-hydration so it can be tested without
// importing every component in this package. Called here, at the point the
// old inline block occupied, so its timing relative to the rest of this
// module is unchanged.
hydrateSessionFromCookie();

// Install safe custom element registration BEFORE any component imports.
// This prevents NotSupportedError when platform-shell is loaded both via
// CDN script tag and npm import in the same page.
import { installSafeDefine } from './utils/safe-define';
installSafeDefine();

// Components
export { MoyaPlatformHeader } from './components/platform-header';
export { MoyaAppSwitcher } from './components/app-switcher';
export { MoyaNotificationBell } from './components/notification-bell';
export { MoyaUserMenu } from './components/user-menu';
export { MoyaContextSwitcher } from './components/context-switcher';
export { MoyaOrganizationSwitcher } from './components/organization-switcher';
export { MoyaGlobalSearch } from './components/global-search';
export { MoyaMessengerIcon } from './components/messenger-icon';
export { MoyaMessengerWidget } from './components/messenger-widget';
export { MoyaMessengerChat } from './components/messenger-chat';
export { MoyaAppContext } from './components/app-context';

// Services
export {
  configure,
  configurePresence,
  configureNotifications,
  configureIdentity,
  getApiBaseUrl,
  getPresenceBaseUrl,
  getNotificationsBaseUrl,
  getIdentityBaseUrl,
  setToken,
  ApiError,
  portalAPI,
  authAPI,
  notificationsAPI,
  presenceAPI,
  settingsAPI,
} from './services/api';
export { wsService } from './services/websocket';
export { eventBus, Events } from './services/events';
export { brandingService, type BrandingConfig, type TenantBranding } from './services/branding';
export { messengerWsService } from './services/messenger-websocket';
export { messengerAPI } from './services/messenger-api';

// Types
export type {
  App,
  User,
  Organization,
  Notification,
  NotificationType,
  NotificationPreference,
  PlatformConfig,
  ContextType,
  CustomMenuItem,
  // Menu-contribution (P1) types
  MenuRegion,
  MenuItemSnapshot,
  MoyaMenuUpdateEvent,
  MoyaMenuSelectEvent,
  // User presence types
  UserStatus,
  UserPresenceUpdate,
  QuickSettings,
  // Navigation / app context types
  BreadcrumbItem,
  NavigationContextUpdate,
  // Event types
  MoyaAppSwitchEvent,
  MoyaNotificationClickEvent,
  MoyaContextSwitchEvent,
  MoyaOrganizationSwitchEvent,
  MoyaLogoutEvent,
  MoyaSearchQueryEvent,
  MoyaSearchSubmitEvent,
  MoyaSearchClearEvent,
  MoyaMessengerOpenEvent,
  MoyaMessengerCloseEvent,
  MoyaMessageClickEvent,
  MoyaStatusChangeEvent,
  MoyaSettingChangeEvent,
  MoyaNavigationContextEvent,
  MoyaBreadcrumbClickEvent,
} from './types';

// Messenger types
export type {
  Message,
  Conversation,
  MessengerParticipant,
  MessageReaction,
  FileAttachment,
  TypingIndicator,
  PresenceUpdate,
  MessengerWidgetMode,
  MessengerWidgetState,
  MessengerConfig,
} from './types/messenger';

// Utility exports
export { installSafeDefine } from './utils/safe-define';

// Auto-register all components when imported
import './components/platform-header';
import './components/app-switcher';
import './components/notification-bell';
import './components/user-menu';
import './components/context-switcher';
import './components/organization-switcher';
import './components/global-search';
import './components/messenger-icon';
import './components/messenger-widget';
import './components/messenger-chat';
import './components/app-context';
